// src/modules/app-views/_apps/appmulti/plans/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import PlansInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import plansInitActionButton from './pages/Init/actions/actionButton';
import plansInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import PlansDocument from './pages/Document';
import plansDocumentActionButton from './pages/Document/actions/actionButton';
import plansDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import plansDocumentMenuButton from './pages/Document/actions/menuButton';
import plansDocumentDecorateRequest from './pages/Document/decorators/request';
import plansDocumentDecorateResponse from './pages/Document/decorators/response';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import { createdAtItem, updatedAtItem } from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import seedPlansParameters from './seed';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import {
  planPriceItem,
  exchangeRateItem,
  coverageCountItem,
  travelProfileItem,
  planDestinationsItem,
  apiOriginalNameItem,
} from './pages/Document/cardHeaderItems';
import PlansPreviewBody from './pages/Init/components/PlansPreviewBody';
import PlansPreviewHeader from './pages/Init/components/PlansPreviewHeader';
import PlansPreviewActions from './pages/Init/components/PlansPreviewActions';
import PlansParametersModal from './pages/Init/modals/PlansParametersModal';
import PlansToolbarLeft from './pages/Init/components/PlansToolbarLeft';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'plans';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: PlansParametersModal,
  parametersSeed: seedPlansParameters,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: 'plans_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <PlansInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.PLANS.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['documentCode', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // PREVIEW DRAWER
    // ───────────────────────────────────────────────────────────────────────
    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'plan_preview',
        header: props => <PlansPreviewHeader {...props} />,
        body: props => <PlansPreviewBody {...props} />,
        actions: props => <PlansPreviewActions {...props} />,
        footer: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actionsInContent: true,
      leftContent: <PlansToolbarLeft />,
      actions: ['refresh', plansInitActionButton(), plansInitMenuButton()],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['records', 'plans'],
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <PlansDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.PLANS.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: plansDocumentDecorateRequest,
      response: plansDocumentDecorateResponse,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        priceType: 'daily',
        currency: 'USD',
        grievanceLevel: 'none',
        status: 'in_analysis',
      },
      actions: [
        'editRefresh',
        plansDocumentActionButton(),
        plansDocumentMenuButton(),
        plansDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: true,
        placeholder: 'product',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['records', 'plans'],
      },

      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_plan" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn
            {...props}
            items={[
              apiOriginalNameItem,
              coverageCountItem,
              travelProfileItem,
              planDestinationsItem,
            ]}
          />
        ),
        column2: props => (
          <CardHeaderColumn
            {...props}
            items={[createdAtItem, updatedAtItem, planPriceItem, exchangeRateItem]}
          />
        ),
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
